import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Wrench, Plus, Trash2, Clock, Calendar as CalendarIcon, Flag, CircleHelp, EyeOff } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  adminListWorkshopEvents,
  adminCreateWorkshopEvent,
  adminDeleteWorkshopEvent,
  adminClassifyWorkshopEvent,
  adminGooglePollNow,
} from '@/lib/api';

// Visual config per classification bucket
const CLASS_META = {
  dyno:         { label: 'Dyno Booking',   icon: Flag,       cls: 'border-green-700 bg-green-900/30 text-green-300' },
  workshop:     { label: 'Workshop',       icon: Wrench,     cls: 'border-amber-700 bg-amber-900/30 text-amber-300' },
  unclassified: { label: 'Unclassified',   icon: CircleHelp, cls: 'border-zinc-700 bg-zinc-900 text-zinc-300' },
  ignored:      { label: 'Ignored',        icon: EyeOff,     cls: 'border-zinc-800 bg-zinc-950 text-zinc-500' },
};

export default function WorkshopSchedule() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [polling, setPolling] = useState(false);
  const [reclassifyingId, setReclassifyingId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    blocks_bookings: true,
  });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await adminListWorkshopEvents();
      setEvents(res.data || []);
    } catch (_e) {
      toast.error('Failed to load workshop events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleSyncNow = async () => {
    setPolling(true);
    try {
      const res = await adminGooglePollNow();
      const d = res.data || {};
      const parts = [];
      if (d.imported) parts.push(`${d.imported} new`);
      if (d.updated) parts.push(`${d.updated} updated`);
      if (d.created_bookings) parts.push(`${d.created_bookings} dyno booking(s) created`);
      if (d.removed) parts.push(`${d.removed} removed`);
      toast.success(parts.length ? `Google sync: ${parts.join(', ')}` : 'Already in sync');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Google sync failed');
    } finally {
      setPolling(false);
    }
  };

  const openAddDialog = () => {
    const now = new Date();
    const start = new Date(now.getTime() + 60 * 60 * 1000); // +1h
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000); // +2h after start
    const pad = (n) => String(n).padStart(2, '0');
    const toInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm({ title: '', description: '', start_time: toInput(start), end_time: toInput(end), blocks_bookings: true });
    setShowDialog(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.start_time || !form.end_time) {
      toast.error('Title, start, and end are required');
      return;
    }
    if (form.end_time <= form.start_time) {
      toast.error('End must be after start');
      return;
    }
    setSaving(true);
    try {
      await adminCreateWorkshopEvent({
        title: form.title.trim(),
        description: form.description.trim() || null,
        start_time: form.start_time + ':00',
        end_time: form.end_time + ':00',
        blocks_bookings: form.blocks_bookings,
      });
      toast.success('Workshop event added');
      setShowDialog(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add workshop event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ev) => {
    const msg = ev.source === 'google_external'
      ? 'Remove this imported event from the app? (Google event stays untouched; it will re-import on the next sync.)'
      : 'Delete this workshop event? It will also be removed from your Google Calendar.';
    if (!window.confirm(msg)) return;
    try {
      await adminDeleteWorkshopEvent(ev.id);
      toast.success('Deleted');
      await refresh();
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  const handleReclassify = async (ev, newClass) => {
    if (newClass === ev.classification) return;
    setReclassifyingId(ev.id);
    try {
      await adminClassifyWorkshopEvent(ev.id, newClass);
      toast.success(`Reclassified to ${CLASS_META[newClass].label}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reclassify failed');
    } finally {
      setReclassifyingId(null);
    }
  };

  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.end_time) >= now);
  const past = events.filter((e) => new Date(e.end_time) < now);
  const imported = upcoming.filter((e) => e.source === 'google_external');
  const manual = upcoming.filter((e) => e.source !== 'google_external');

  const classificationBadge = (ev) => {
    const meta = CLASS_META[ev.classification] || CLASS_META.unclassified;
    const Icon = meta.icon;
    return (
      <span className={`px-2 py-0.5 border ${meta.cls} text-xs uppercase tracking-wider flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {meta.label}
      </span>
    );
  };

  const sourceBadge = (ev) => {
    if (ev.source === 'google_external') {
      return <span className="px-2 py-0.5 border border-blue-700 bg-blue-900/30 text-blue-300 text-xs uppercase tracking-wider">From Google</span>;
    }
    return <span className="px-2 py-0.5 border border-zinc-700 bg-zinc-900 text-zinc-300 text-xs uppercase tracking-wider">In-app</span>;
  };

  const renderRow = (ev) => {
    const start = parseISO(ev.start_time);
    const end = parseISO(ev.end_time);
    const isGoogle = ev.source === 'google_external';
    return (
      <div
        key={ev.id}
        className="glass p-4 border border-zinc-800 bg-zinc-900/50 hover:border-zinc-700 transition-colors"
        data-testid={`workshop-event-${ev.id}`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="text-white font-medium truncate">{ev.title}</h4>
              {sourceBadge(ev)}
              {isGoogle && classificationBadge(ev)}
              {ev.blocks_bookings && (
                <span className="px-2 py-0.5 border border-red-700 bg-red-900/30 text-red-300 text-xs uppercase tracking-wider">
                  Blocks Dyno
                </span>
              )}
              {ev.linked_booking_id && (
                <span className="px-2 py-0.5 border border-green-700/60 bg-green-900/20 text-green-400 text-xs uppercase tracking-wider">
                  Booking Created
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-400 flex-wrap">
              <span className="flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" />
                {format(start, 'EEE, d MMM yyyy')}
              </span>
              <span className="flex items-center gap-1 font-mono">
                <Clock className="w-3 h-3" />
                {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
              </span>
            </div>
            {ev.description && (
              <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{ev.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isGoogle && (
              <Select
                value={ev.classification}
                disabled={reclassifyingId === ev.id}
                onValueChange={(val) => handleReclassify(ev, val)}
              >
                <SelectTrigger
                  className="h-8 w-[160px] bg-zinc-950 border-zinc-700 text-xs"
                  data-testid={`workshop-reclassify-${ev.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="dyno" data-testid={`reclassify-opt-dyno-${ev.id}`}>Dyno Booking</SelectItem>
                  <SelectItem value="workshop" data-testid={`reclassify-opt-workshop-${ev.id}`}>Workshop</SelectItem>
                  <SelectItem value="unclassified" data-testid={`reclassify-opt-unclassified-${ev.id}`}>Unclassified</SelectItem>
                  <SelectItem value="ignored" data-testid={`reclassify-opt-ignored-${ev.id}`}>Ignored</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(ev)}
              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
              data-testid={`workshop-delete-${ev.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="workshop-schedule">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white flex items-center gap-2">
          <Wrench className="w-5 h-5" /> Workshop Schedule
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleSyncNow}
            disabled={polling}
            variant="outline"
            className="border-blue-800 text-blue-300 hover:bg-blue-900/30"
            data-testid="workshop-sync-now-btn"
          >
            {polling ? 'Syncing…' : 'Sync Google Now'}
          </Button>
          <Button onClick={openAddDialog} className="bg-green-600 hover:bg-green-700" data-testid="workshop-add-btn">
            <Plus className="w-4 h-4 mr-2" />Add Workshop Event
          </Button>
        </div>
      </div>
      <p className="text-zinc-500 text-sm">
        Google Calendar events are auto-classified by title keyword:
        <span className="text-green-400"> &nbsp;“dyno”</span> → creates a confirmed dyno booking,
        <span className="text-amber-400"> &nbsp;“workshop”</span> → blocks the slots,
        everything else → <span className="text-zinc-300">Unclassified</span> until you reclassify it below. Syncs every 5 minutes.
      </p>

      {/* Imported from Google */}
      <section className="space-y-2" data-testid="imported-google-section">
        <h3 className="text-zinc-300 uppercase tracking-wider text-sm flex items-center gap-2">
          <CalendarIcon className="w-4 h-4" /> Imported from Google Calendar
          <span className="text-zinc-600">({imported.length})</span>
        </h3>
        {loading ? (
          <div className="text-zinc-500 text-center py-6">Loading…</div>
        ) : imported.length === 0 ? (
          <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 text-center text-zinc-500 text-sm">
            Nothing imported yet. Add an event to your Google Calendar with “dyno” or “workshop” in the title.
          </div>
        ) : (
          <div className="space-y-2">
            {imported.map(renderRow)}
          </div>
        )}
      </section>

      {/* Manual In-app events */}
      <section className="space-y-2 pt-2" data-testid="manual-section">
        <h3 className="text-zinc-300 uppercase tracking-wider text-sm flex items-center gap-2">
          <Wrench className="w-4 h-4" /> Added in-app
          <span className="text-zinc-600">({manual.length})</span>
        </h3>
        {!loading && manual.length === 0 ? (
          <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 text-center text-zinc-500 text-sm">
            No manual workshop events. Tap “Add Workshop Event” to block out time for brake jobs, service work, etc.
          </div>
        ) : (
          <div className="space-y-2">
            {manual.map(renderRow)}
          </div>
        )}
      </section>

      {past.length > 0 && (
        <details className="mt-6">
          <summary className="text-zinc-500 uppercase tracking-wider text-sm cursor-pointer">Past ({past.length})</summary>
          <div className="space-y-2 mt-3 opacity-60">
            {past.map(renderRow)}
          </div>
        </details>
      )}

      {/* Add Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">
              Add Workshop Event
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Block out time for workshop repairs, appointments, or anything else.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                placeholder="e.g., Brake job - Mitsubishi Evo"
                data-testid="workshop-title-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Start *</Label>
                <Input
                  type="datetime-local"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 font-mono"
                  data-testid="workshop-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">End *</Label>
                <Input
                  type="datetime-local"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 font-mono"
                  data-testid="workshop-end-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                placeholder="Any notes…"
                data-testid="workshop-desc-input"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-1">
              <Checkbox
                checked={form.blocks_bookings}
                onCheckedChange={(v) => setForm({ ...form, blocks_bookings: !!v })}
                className="mt-0.5 border-zinc-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-500"
                data-testid="workshop-blocks-checkbox"
              />
              <span className="text-sm text-zinc-300">
                Block customer dyno bookings during this time
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving} className="border-zinc-700 text-zinc-300">Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="workshop-save-btn"
            >
              {saving ? 'Saving…' : 'Add Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
